//
// # LvsErrorRow
//
// Single error row in the LVS panel.
// Clicking highlights the relevant layout elements.
//

import React from "react";
import {
  Unplug,
  Zap,
  Package,
  PackagePlus,
  Pin,
  PinOff,
  CircleOff,
  GitBranch,
  AlertTriangle,
} from "lucide-react";
import { useWorkspace } from "../../context";
import type { LvsError } from "./types";

// Maximum pins to show before truncating with "+N more"
const MAX_PINS_SHOWN = 5;

interface LvsErrorRowProps {
  error: LvsError;
  index: number;
}

// Icon and color for each error kind
const errorStyles: Record<
  string,
  { icon: React.ReactNode; bgClass: string; iconClass: string }
> = {
  open: {
    icon: <Unplug size={12} />,
    bgClass: "bg-orange-500/10 hover:bg-orange-500/20",
    iconClass: "text-orange-400",
  },
  short: {
    icon: <Zap size={12} />,
    bgClass: "bg-red-500/10 hover:bg-red-500/20",
    iconClass: "text-red-400",
  },
  missing_instance: {
    icon: <Package size={12} />,
    bgClass: "bg-yellow-500/10 hover:bg-yellow-500/20",
    iconClass: "text-yellow-400",
  },
  extra_instance: {
    icon: <PackagePlus size={12} />,
    bgClass: "bg-blue-500/10 hover:bg-blue-500/20",
    iconClass: "text-blue-400",
  },
  missing_pin: {
    icon: <PinOff size={12} />,
    bgClass: "bg-purple-500/10 hover:bg-purple-500/20",
    iconClass: "text-purple-400",
  },
  extra_pin: {
    icon: <Pin size={12} />,
    bgClass: "bg-cyan-500/10 hover:bg-cyan-500/20",
    iconClass: "text-cyan-400",
  },
  // Topology-based matching errors
  unmatched_schematic_net: {
    icon: <CircleOff size={12} />,
    bgClass: "bg-orange-500/10 hover:bg-orange-500/20",
    iconClass: "text-orange-400",
  },
  unmatched_layout_net: {
    icon: <CircleOff size={12} />,
    bgClass: "bg-orange-500/10 hover:bg-orange-500/20",
    iconClass: "text-orange-400",
  },
  unmatched_schematic_device: {
    icon: <GitBranch size={12} />,
    bgClass: "bg-yellow-500/10 hover:bg-yellow-500/20",
    iconClass: "text-yellow-400",
  },
  unmatched_layout_device: {
    icon: <GitBranch size={12} />,
    bgClass: "bg-yellow-500/10 hover:bg-yellow-500/20",
    iconClass: "text-yellow-400",
  },
  structural_mismatch: {
    icon: <AlertTriangle size={12} />,
    bgClass: "bg-red-500/10 hover:bg-red-500/20",
    iconClass: "text-red-400",
  },
};

export function LvsErrorRow({ error, index }: LvsErrorRowProps) {
  const workspace = useWorkspace();
  const style = errorStyles[error.kind] ?? errorStyles.open;

  const handleClick = () => {
    workspace.highlightLvsError(error);
  };

  // Format the error label with truncation for long pin lists
  const formatLabel = (): string => {
    if (error.pins && error.pins.length > 0) {
      // Filter out empty strings
      const validPins = error.pins.filter((p) => p.length > 0);
      if (validPins.length === 0) {
        // Fall through to other label options
      } else if (validPins.length <= MAX_PINS_SHOWN) {
        return validPins.join(", ");
      } else {
        // Truncate long lists: show first N + count
        const shown = validPins.slice(0, MAX_PINS_SHOWN).join(", ");
        return `${shown} +${validPins.length - MAX_PINS_SHOWN} more`;
      }
    }
    if (error.instance) {
      return error.instance;
    }
    if (error.net) {
      return error.net;
    }
    return `Error ${index + 1}`;
  };

  return (
    <button
      className={`w-full px-2 py-1.5 rounded flex items-center gap-2 text-left transition-colors cursor-pointer ${style.bgClass}`}
      onClick={handleClick}
    >
      <span className={style.iconClass}>{style.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-medium truncate">{formatLabel()}</div>
        <div className="text-[10px] text-[var(--color-ide-text-dim)] truncate">
          {error.message}
        </div>
      </div>
    </button>
  );
}
